import React, { useEffect, useMemo } from 'react';
import { Combobox } from '@withjoy/joykit';
import type { ComboboxOption } from '@withjoy/joykit/components/Combobox/Combobox.types';
import type { LanguagePickerV2Props } from './LanguagePickerV2.types';
import { formatLanguage, formatUnknownLanguage } from './LanguagePickerV2.utils.internal';
import { normalizeLanguageCode } from './LanguagePickerV2.utils';
import { useTranslation } from '@shared/core';
import i18n from '@shared/core/i18n/i18n';
import { useLanguageOptions } from './useLanguageOptions.internal';
import { addError } from '@shared/utils/logger';
import { IconUnion } from '@withjoy/joykit/icons';

export const LanguagePickerV2: React.FC<LanguagePickerV2Props> = props => {
  const {
    disabled,
    placeholder,
    mode,
    name,
    onBlur,
    onOpenChange,
    allowedLanguageCodes,
    triggerVariant,
    optionLayout = 'inline',
    searchable = true,
    buttonProps: buttonPropsOverride,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-errormessage': ariaErrorMessage,
  } = props;
  const { tq } = useTranslation('languagePickerV2');
  const allLanguageOptions = useLanguageOptions();
  const languageOptions = useMemo(
    () => (allowedLanguageCodes ? allLanguageOptions.filter(option => allowedLanguageCodes.includes(option.value)) : allLanguageOptions),
    [allLanguageOptions, allowedLanguageCodes],
  );

  const selectedCodes = useMemo(
    () => (props.multiple ? props.value.map(code => normalizeLanguageCode(code)) : [normalizeLanguageCode(props.value)]),
    [props.multiple, props.value],
  );

  // Options mode (vs children) so Combobox renders selected-state indicators itself.
  // 'stacked' mirrors the currency selector: a two-line, flagless row with the
  // native language name over its region (ComboboxOption.description).
  // Single-selection only: a stacked row drops the flag and reads its selected state from
  // the row highlight, which multi-select can't rely on.
  const isStacked = optionLayout === 'stacked' && !props.multiple;
  const { options, selectedDisplays, selectedFlags } = useMemo(() => {
    const displays: string[] = [];
    const flags: string[] = [];

    const items: ComboboxOption[] = languageOptions.map(lang => {
      const isSelected = selectedCodes.includes(lang.value);

      if (isStacked) {
        // Stacked rows show the native name + region, so the formatted (flag + label) display
        // is never used here — only the raw flag, for the flag-trigger variant.
        if (isSelected) {
          displays.push(lang.nativeLanguage);
          flags.push(lang.flag);
        }
        // keywords keep the translated + native names searchable even though the
        // visible label is native-only (e.g. typing "spanish" still finds "Español").
        return { value: lang.value, label: lang.nativeLanguage, description: lang.nativeRegion, keywords: [lang.label, lang.nativeRegion, lang.value] };
      }

      const formatted = formatLanguage(lang.value, lang.flag, lang.label);
      if (isSelected) {
        displays.push(formatted.display);
        flags.push(formatted.flag);
      }

      return { value: lang.value, label: formatted.display, keywords: [lang.label, lang.value] };
    });

    return { options: items, selectedDisplays: displays, selectedFlags: flags };
  }, [languageOptions, selectedCodes, isStacked]);

  // Count from selectedCodes (not matched displays) so stored codes missing from the
  // options list still surface; a lone unknown code falls back to its raw value.
  const selectedCount = props.multiple ? selectedCodes.length : props.value ? 1 : 0;
  const displayValue =
    selectedCount > 1
      ? tq.plural(selectedCount, {
          one: tq`${String(selectedCount)} languages`,
          other: tq`${String(selectedCount)} languages`,
        })
      : selectedCount === 1
        ? (selectedDisplays[0] ?? formatUnknownLanguage(selectedCodes[0], i18n.language))
        : '';

  // Flag variant collapses the trigger to a Globe icon + flag; icon variant to the
  // Globe alone (no flag, no text). Both are single-selection only; an unknown
  // stored code has no flag, so the icon alone still marks the control.
  const isFlagTrigger = triggerVariant === 'flag' && !props.multiple;
  const isIconTrigger = triggerVariant === 'icon' && !props.multiple;
  const isIconOnlyTrigger = isFlagTrigger || isIconTrigger;
  const triggerDisplayValue = isIconTrigger ? '' : isFlagTrigger ? (selectedFlags[0] ?? '') : displayValue;

  const hasUnknownValue = selectedCount > 0 && selectedDisplays.length < selectedCount;
  useEffect(() => {
    if (hasUnknownValue) {
      addError(`Invalid language value(s) in LanguagePickerV2: ${selectedCodes.join(', ')}`, { selectedCodes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnknownValue]);

  const sharedProps = {
    options,
    // Icon-only trigger shows nothing but the globe, so suppress the placeholder text.
    placeholder: isIconTrigger ? '' : placeholder || tq`Select language...`,
    displayValue: triggerDisplayValue,
    disabled,
    name,
    onBlur,
    onOpenChange,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-errormessage': ariaErrorMessage,
    searchPlaceholder: tq`Search languages...`,
    emptyMessage: tq`No languages found.`,
    searchable,
  };

  if (mode === 'button') {
    const buttonProps = { intent: 'neutral', shape: 'rounded', ...buttonPropsOverride, ...(isIconOnlyTrigger && { startIcon: <IconUnion size={16} /> }) } as const;
    return props.multiple ? (
      <Combobox
        mode="button"
        multiple
        buttonProps={buttonProps}
        dialogTitle={tq`Choose Languages`}
        value={selectedCodes}
        onValueChange={props.onChange}
        {...sharedProps}
      />
    ) : (
      <Combobox
        mode="button"
        buttonProps={buttonProps}
        dialogTitle={tq`Choose Language`}
        value={selectedCodes[0]}
        onValueChange={props.onChange}
        {...sharedProps}
      />
    );
  }

  return props.multiple ? (
    <Combobox
      multiple
      value={selectedCodes}
      onValueChange={props.onChange}
      {...sharedProps}
    />
  ) : (
    <Combobox
      value={selectedCodes[0]}
      onValueChange={props.onChange}
      {...sharedProps}
    />
  );
};

LanguagePickerV2.displayName = 'LanguagePickerV2';
